import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { courseService } from '../services/courseService';
import { authService } from '../services/authService';
import { userService } from '../services/userService';
import { Course } from '../types';
import { getMediaUrl } from '../utils/media';
import { aboutVideoService, AboutVideoPublic } from '../services/aboutVideoService';
import './AboutPage.css';

const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState<AboutVideoPublic[]>([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [communityStats, setCommunityStats] = useState({
    total_members: 0,
    online_members: 0,
    total_admins: 0,
  });
  const isAuthenticated = authService.isAuthenticated();

  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  const extractYouTubeId = (url: string): string | null => {
    try {
      const parsedUrl = new URL(url);

      // https://www.youtube.com/watch?v=VIDEO_ID
      if (parsedUrl.searchParams.get('v')) {
        return parsedUrl.searchParams.get('v');
      }

      // https://youtu.be/VIDEO_ID or https://www.youtube.com/embed/VIDEO_ID
      const pathnameParts = parsedUrl.pathname.split('/').filter(Boolean);
      if (pathnameParts.length > 0) {
        return pathnameParts[pathnameParts.length - 1];
      }

      return null;
    } catch {
      // Fallback for invalid URL
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch courses
        const coursesData = await courseService.getCourses();
        setCourses(coursesData.courses);

        // Fetch community stats
        const stats = await userService.getCommunityStats();
        setCommunityStats(stats);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const activeVideos = await aboutVideoService.getActiveVideos();
        setVideos(activeVideos);
      } catch (error) {
        console.error('Error fetching About videos:', error);
      } finally {
        setVideosLoading(false);
      }
    };

    fetchVideos();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Community Header */}
      <div className="bg-white rounded-2xl shadow-soft border border-gray-200 p-6 sm:p-8 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              {t('about.header.title')}
            </h1>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                🎬 {t('about.header.badgePlatform')}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {t('about.header.badgeCreators', { val: formatNumber(communityStats.total_members) })}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {t('about.header.badgeFree')}
              </span>
            </div>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center gap-3">
            <span className="text-sm text-gray-600">{t('about.header.by')}</span>
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <span className="text-xl">⭐</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Welcome Section */}
          <div className="bg-white rounded-2xl shadow-soft border border-gray-200 p-6 sm:p-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              {t('about.welcome.title')}
            </h2>
            <p className="text-gray-600 mb-6 text-lg">
              {t('about.welcome.subtitle')}
            </p>

            <div className="space-y-4 mb-8">
              {[
                t('about.welcome.benefits.shareKey'),
                t('about.welcome.benefits.growth'),
                t('about.welcome.benefits.trends'),
                t('about.welcome.benefits.chat'),
                t('about.welcome.benefits.seo'),
                t('about.welcome.benefits.monetization'),
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <span className="text-green-500 text-xl flex-shrink-0 mt-0.5">✅</span>
                  <span className="text-gray-700 text-base leading-relaxed">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="mt-8">
              {isAuthenticated ? (
                <Link
                  to="/classes"
                  className="inline-block w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-xl font-semibold text-lg hover:shadow-large transition-all duration-200 transform hover:-translate-y-0.5 text-center"
                >
                  {t('about.welcome.exploreButton')}
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="inline-block w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold text-lg hover:shadow-large transition-all duration-200 transform hover:-translate-y-0.5 text-center"
                >
                  {t('about.welcome.joinButton')}
                </Link>
              )}
            </div>
          </div>

          {/* YouTube Videos from Admin */}
          <div className="bg-white rounded-2xl shadow-soft border border-gray-200 p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {t('about.videos.title')}
              </h2>
              {!isAuthenticated && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  🔒 {t('about.videos.loginToView')}
                </span>
              )}
            </div>

            {videosLoading ? (
              <p className="text-gray-500">{t('about.videos.loading')}</p>
            ) : videos.length === 0 ? (
              <p className="text-gray-500">{t('about.videos.empty')}</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {videos.map((video, index) => {
                  const videoId = extractYouTubeId(video.youtube_link);
                  const thumbnailUrl = videoId
                    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                    : undefined;
                  const isLocked = !isAuthenticated;

                  return (
                    <div
                      key={`${video.youtube_link}-${index}`}
                      className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden shadow-sm"
                    >
                      <div className="aspect-video relative bg-black">
                        {!isLocked && videoId ? (
                          <iframe
                            className="w-full h-full"
                            src={`https://www.youtube.com/embed/${videoId}`}
                            title={video.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        ) : (
                          <>
                            {thumbnailUrl && (
                              <img
                                src={thumbnailUrl}
                                alt={video.title}
                                className="w-full h-full object-cover opacity-70 blur-sm"
                                loading="lazy"
                              />
                            )}
                            <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col items-center justify-center gap-3">
                              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-white bg-opacity-90">
                                <svg
                                  className="w-8 h-8 text-gray-800"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                  />
                                </svg>
                              </div>
                              <Link
                                to="/login"
                                className="px-4 py-2 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-150"
                              >
                                {t('about.videos.loginToUnlock')}
                              </Link>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-lg text-gray-900 line-clamp-2 mb-1">
                          {video.title}
                        </h3>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Courses Section */}
          {false && (<div className="bg-white rounded-2xl shadow-soft border border-gray-200 p-6 sm:p-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
              {isAuthenticated ? t('about.course.title') : `🔓 ${t('about.course.lockedTitle')}`}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {courses.length > 0 ? (
                // Show actual courses when loaded
                (isAuthenticated ? courses.slice(0, 6) : courses.slice(0, 2)).map((course, index) => {
                  const isBlurred = !isAuthenticated && index < 2;
                  return (
                    <Link
                      key={course.id}
                      to={isBlurred ? '/login' : `/courses/${course.id}`}
                      className={`group bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-medium transition-all duration-200 transform hover:-translate-y-1 animate-fadeIn ${isBlurred ? 'opacity-60 cursor-pointer' : ''}`}
                    >
                      {course.thumbnail && (
                        <div className="aspect-video overflow-hidden bg-gray-200 relative">
                          <img
                            src={getMediaUrl(course.thumbnail)}
                            alt={course.title}
                            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${isBlurred ? 'filter blur-lg' : ''}`}
                            loading="lazy"
                          />
                          {isBlurred && (
                            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                              {/* YouTube Logo Background */}
                              <div className="absolute inset-0 flex items-center justify-center opacity-20">
                                <svg className="w-32 h-32" viewBox="0 0 24 24" fill="#FF0000">
                                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                </svg>
                              </div>
                              {/* Lock Icon */}
                              <div className="relative z-10">
                                <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="p-5">
                        <h3 className={`font-semibold text-lg mb-2 line-clamp-2 transition-colors ${isBlurred ? 'text-gray-400' : 'text-gray-900 group-hover:text-primary-600'}`}>
                          {isBlurred ? `🔐 ${t('about.course.lockedTitle')}` : course.title}
                        </h3>
                        <p className={`text-sm mb-4 line-clamp-2 ${isBlurred ? 'text-gray-400' : 'text-gray-600'}`}>
                          {isBlurred ? t('about.course.lockedDesc') : course.description}
                        </p>
                        {!isBlurred && (
                          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                            <span className="font-bold text-green-600">
                              {course.price === 0 ? t('about.course.free') : `${course.price.toLocaleString('vi-VN')} VNĐ`}
                            </span>
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              {course.level === 'beginner' ? t('about.course.level.beginner') : course.level === 'intermediate' ? t('about.course.level.intermediate') : course.level === 'advanced' ? t('about.course.level.advanced') : course.level}
                            </span>
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 text-lg">{loading ? t('common.loading') : t('about.courses.empty')}</p>
                </div>
              )}
            </div >
            {isAuthenticated && courses.length > 6 && (
              <div className="text-center mt-8">
                <Link
                  to="/classes"
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 font-semibold text-lg transition-colors"
                >
                  {t('about.courses.viewAll')}
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            )}
          </div>)}
        </div >

        {/* Sidebar */}
        < div className="space-y-6" >
          {/* Community Stats */}
          < div className="bg-white rounded-2xl shadow-soft border border-gray-200 p-6" >
            <h3 className="text-xl font-bold text-gray-900 mb-6">{t('about.sidebar.communityStats')}</h3>
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <span className="text-3xl font-bold text-gray-900">{formatNumber(communityStats.total_members)}</span>
                <span className="text-gray-600">{t('about.sidebar.members')}</span>
              </div>
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <span className="text-3xl font-bold text-gray-900">{communityStats.online_members}</span>
                <span className="text-gray-600">{t('about.sidebar.online')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-gray-900">{communityStats.total_admins}</span>
                <span className="text-gray-600">{t('about.sidebar.admins')}</span>
              </div>
            </div>
            {
              !isAuthenticated && (
                <Link
                  to="/login"
                  className="block w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold text-center hover:shadow-medium transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  {t('about.sidebar.join')}
                </Link>
              )
            }
          </div >

          {/* Description */}
          < div className="bg-white rounded-2xl shadow-soft border border-gray-200 p-6" >
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('about.sidebar.descriptionTitle')}</h3>
            <p className="text-gray-600 leading-relaxed">
              {t('about.sidebar.description')}
            </p>
          </div >
        </div >
      </div >
    </div >
  );
};

export default AboutPage;
